import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, storage } from '../config/firebase';

const FirebaseContext = createContext();

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export const FirebaseProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize and monitor auth state
  useEffect(() => {
    console.log('Initializing Firebase auth state observer...');
    
    const unsubscribe = onAuthStateChanged(
      auth,
      (updatedUser) => {
        console.log('Auth state changed:', updatedUser ? `User: ${updatedUser.uid}` : 'No user');
        setUser(updatedUser);
        setLoading(false);
        setInitialized(true);
      },
      (error) => {
        console.error('Auth state observer error:', error);
        setError(error.message);
        setLoading(false);
        setInitialized(true);
      }
    );

    // Clean up
    return () => {
      console.log('Cleaning up auth state observer');
      unsubscribe();
    };
  }, []); // Empty dependency array - only run once on mount

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Sign in successful:', userCredential.user.uid);
      return userCredential.user;
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      console.log('Sign out successful');
    } catch (error) {
      console.error('Sign out error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Firestore CRUD operations
  const createDocument = async (collectionName, data, files = {}) => {
    try {
      setLoading(true);
      console.log(`Creating document in collection: ${collectionName}`, data);
      
      // Upload any files first and get their URLs
      const uploadedFiles = {};
      for (const [field, file] of Object.entries(files)) {
        if (file) {
          const path = `${collectionName}/${Date.now()}-${file.name}`;
          const url = await uploadImage(file, path);
          uploadedFiles[field] = url;
        }
      }

      // Combine the data with uploaded file URLs
      const docData = {
        ...data,
        ...uploadedFiles,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, collectionName), docData);
      console.log('Document created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAllDocuments = async (collectionName) => {
    if (!initialized) {
      console.log('Firebase not yet initialized');
      return [];
    }
    try {
      console.log(`Getting all documents from collection: ${collectionName}`);
      const querySnapshot = await getDocs(collection(db, collectionName));
      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`Retrieved ${documents.length} documents from ${collectionName}`);
      return documents;
    } catch (error) {
      console.error(`Error getting documents from ${collectionName}:`, error);
      setError(error.message);
      throw error;
    }
  };

  const getDocument = async (collectionName, docId) => {
    try {
      setLoading(true);
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateDocument = async (collectionName, docId, data) => {
    try {
      setLoading(true);
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (collectionName, docId) => {
    try {
      setLoading(true);
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file, path) => {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    initialized,
    signIn,
    signOut,
    createDocument,
    getAllDocuments,
    getDocument,
    updateDocument,
    deleteDocument,
    uploadImage
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}; 